package com.arena_web.pages.news

import androidx.compose.runtime.*
import com.arena_web.data.dto.NewsArticleDto
import com.arena_web.di.LocalAppContainer
import com.arena_web.ui.components.navigation.SidebarTab
import com.arena_web.ui.layouts.AppPageLayout
import com.arena_web.ui.sections.news.NewsArticleDetail
import com.arena_web.ui.utils.isMobile
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext

// Arena News article detail. Public content; the backend detail endpoint also
// bumps views_count, which feeds the feed's "popular" sort. On phones the top
// bar swaps the logo for a back arrow (drill-in surface, like Chat).
@Page("/news/{id}")
@Composable
fun NewsArticlePage() {
    val ctx = rememberPageContext()
    val mobile = isMobile()
    val container = LocalAppContainer.current
    val repository = container.newsRepository

    val articleId = ctx.route.path.substringAfterLast("/")

    var article by remember { mutableStateOf<NewsArticleDto?>(null) }
    var related by remember { mutableStateOf<List<NewsArticleDto>>(emptyList()) }
    var notFound by remember { mutableStateOf(false) }

    LaunchedEffect(articleId) {
        article = null
        related = emptyList()
        notFound = false
        val locale = com.arena_web.utils.Storage.get(com.arena_web.utils.StorageKeys.LANGUAGE)
            ?.takeIf { it.isNotBlank() && it != "en" }
        repository.getArticle(articleId, locale)
            .onSuccess { loaded ->
                article = loaded
                // Related — strictly hashtag-linked: only articles sharing at
                // least one tag qualify (most overlapping tags first, the
                // feed's recency order as the tie-breaker). No shared tags →
                // the section simply doesn't render.
                repository.getNews(
                    buildMap {
                        put("sport", loaded.sport)
                        put("size", "12")
                        locale?.let { put("locale", it) }
                    }
                )
                    .onSuccess { page ->
                        val myTags = loaded.hashtags.toSet()
                        related = page.items
                            .filter { candidate ->
                                candidate.id != loaded.id &&
                                    candidate.hashtags.any { it in myTags }
                            }
                            .sortedByDescending { candidate ->
                                candidate.hashtags.count { it in myTags }
                            }
                            .take(4)
                    }
            }
            .onFailure { notFound = true }
    }

    AppPageLayout(
        activeTab = SidebarTab.News,
        title = article?.title ?: "News",
        description = article?.summary ?: "",
        mobileReady = true,
        // Phones: no global chrome at all — the cover starts at the very top
        // of the screen with a floating back button over it, and the bottom
        // nav stays out of the reading surface (event-detail pattern).
        mobileHideTopBar = true,
        mobileHideBottomNav = true,
        mobileEdgeToEdge = true,
    ) {
        val current = article
        when {
            current != null -> NewsArticleDetail(article = current, related = related, mobile = mobile)
            notFound -> {
                // Broken/expired link — bounce back to the feed.
                LaunchedEffect(Unit) { ctx.router.navigateTo("/news") }
            }
            else -> {
                // Loading — intentionally empty (no loading indicators).
            }
        }
    }
}
